<script setup lang="ts">
import { dayjs } from '@revolugo/common/utils'
import { BookingStatus } from '@revolugo/graphql/private'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { RDetailsList, RDetailsListItem, RFaIconTag, RTag } from '#atoms'

import type { BookingDetailsListProps } from './BookingDetailsList.types.ts'

const { t } = useI18n()

const props = defineProps<BookingDetailsListProps>()

const googleMapUrl = computed(
  () => `https://www.google.com/maps/search/?api=1&query=${props.hotel.name}`,
)

const hotelUrl = computed(() => `/hotels/${props.hotel.id}`)

const isBreakFastIncluded = computed(
  () => (props.booking.hotelRoomOffer?.breakfastCount || 0) > 0,
)

const nightCount = computed(() =>
  dayjs(props.booking.checkOutDate).diff(props.booking.checkInDate, 'days'),
)

const yourReservation = computed(() => {
  const adultCount = t('adult', {
    count: props.booking.adultCount,
  })

  const childrenCount = props.booking.children
    ? `, ${t('children', { count: props.booking.children.split(',').length })}`
    : ''

  const duration = `${t('during')} ${t('night', {
    count: nightCount.value,
  })}`

  return `${adultCount}${childrenCount} ${duration}`
})

const bookingStatusLabel = computed(() => {
  switch (props.booking.status) {
    case BookingStatus.Confirmed:
      return {
        label: t('status.confirmed'),
        severity: 'success',
      }
    case BookingStatus.Canceled:
      return {
        label: t('status.canceled'),
        severity: 'danger',
      }
    case BookingStatus.InProgress:
      return {
        label: t('status.inProgress'),
        severity: 'warn',
      }
    case BookingStatus.Created:
      return {
        label: t('status.created'),
        severity: 'info',
      }
    case BookingStatus.Failed:
      return {
        label: t('status.failed'),
        severity: 'danger',
      }
    case BookingStatus.PendingConfirm:
      return {
        label: t('status.pendingConfirmation'),
        severity: 'info',
      }
    case BookingStatus.PendingPayment:
      return {
        label: t('status.pendingPayment'),
        severity: 'warn',
      }
    case BookingStatus.QuoteRequest:
      return {
        label: t('status.quoteRequest'),
        severity: 'info',
      }
    default:
      return undefined
  }
})
</script>

<template>
  <div class="flex flex-col gap-2">
    <RDetailsList>
      <RDetailsListItem>
        <template #title>{{ t('status.title') }}:</template>
        <template
          #description
          v-if="bookingStatusLabel"
        >
          <RTag
            :severity="bookingStatusLabel.severity"
            :value="bookingStatusLabel.label"
            data-testid="booking-status"
            class="text-base"
            size="small"
          ></RTag>
        </template>
      </RDetailsListItem>

      <RDetailsListItem>
        <template #title>{{ t('name') }}:</template>
        <template #description>
          <span
            class="body-2-semibold"
            data-testid="booking-customer-lastname"
          >
            {{ booking.customer.lastName }}
          </span>
        </template>
      </RDetailsListItem>

      <RDetailsListItem>
        <template #title>{{ t('firstname') }}:</template>
        <template
          #description
          class="px-[0.4rem] py-1 rounded-md"
        >
          <span
            class="body-2-semibold"
            data-testid="booking-customer-firstname"
          >
            {{ booking.customer.firstName }}
          </span>
        </template>
      </RDetailsListItem>

      <RDetailsListItem>
        <template #title>{{ t('hotel') }}:</template>
        <template #description>
          <a
            class="body-2-semibold text-blue-600 underline visited:text-blue-600"
            :href="hotelUrl"
            target="_blank"
            rel="noopener"
            data-testid="booking-hotel-name"
          >
            {{ hotel.name }}
          </a>
        </template>
      </RDetailsListItem>

      <RDetailsListItem>
        <template #title>{{ t('address') }}:</template>
        <template #description>
          <a
            class="body-2-semibold text-blue-600 underline visited:text-blue-600"
            target="_blank"
            rel="noopener"
            :href="googleMapUrl"
            data-testid="hotel-info"
          >
            {{
              `${hotel.address}, ${hotel.postalCode} ${hotel.city}, ${hotel.countryCode}`
            }}
          </a>
        </template>
      </RDetailsListItem>

      <RDetailsListItem v-if="booking.event.length">
        <template #title>{{ t('event') }}:</template>
        <template #description>
          <span
            class="body-2-semibold"
            data-testid="booking-event"
          >
            {{ booking.event }}
          </span>
        </template>
      </RDetailsListItem>

      <RDetailsListItem>
        <template #title>{{ t('yourReservation') }}:</template>
        <template #description>
          <span
            class="body-2-semibold"
            data-testid="booking-reservation"
          >
            {{ yourReservation }}
          </span>
        </template>
      </RDetailsListItem>
    </RDetailsList>

    <RDetailsList>
      <RDetailsListItem>
        <template #title>
          <span>{{ t('rooms') }}:</span>
        </template>
      </RDetailsListItem>
      <RDetailsListItem
        v-for="(room, index) in booking.hotelRoomOffer.hotelRooms"
      >
        <template #description>
          <span
            :data-testid="`hotel-room-${index}`"
            class="body-2-semibold"
          >
            {{ `${room.count} x ${room.description}` }}
          </span>
          <span class="ml-1 body-2-medium">- {{ room.bedsPretty }}</span>
        </template>
      </RDetailsListItem>
    </RDetailsList>

    <RDetailsList>
      <RDetailsListItem>
        <template #title>{{ t('breakfast') }}:</template>
        <template #description>
          <span
            class="body-2-semibold"
            data-testid="breakfast-option"
          >
            {{
              isBreakFastIncluded
                ? t('breakfastOptions.included')
                : t('breakfastOptions.notIncluded')
            }}
          </span>
        </template>
      </RDetailsListItem>

      <RDetailsListItem v-if="booking.reference">
        <template #title>{{ t('reference') }}:</template>
        <template #description>
          <span
            class="body-2-semibold"
            data-testid="booking-reference"
          >
            {{ booking.reference }}
          </span>
        </template>
      </RDetailsListItem>

      <RDetailsList v-if="booking.hotelRoomOffer.tags">
        <RDetailsListItem>
          <template #title>
            <span>{{ t('tags') }}:</span>
          </template>
        </RDetailsListItem>
        <RDetailsListItem v-for="(tag, index) in booking.hotelRoomOffer.tags">
          <template #description>
            <RFaIconTag
              :tag="{
                bg: tag.bg!,
                color: tag.color!,
                name: tag.name,
                faIcon: tag?.faIcon || undefined,
              }"
              :key="index"
            ></RFaIconTag>
          </template>
        </RDetailsListItem>
      </RDetailsList>
    </RDetailsList>
  </div>
</template>

<style lang="scss"></style>

<i18n lang="json" src="./BookingDetailsList.i18n.json"></i18n>
